import { computed, inject, Injectable, signal } from '@angular/core';
import { MediaApi } from '@app/data/api';
import { MediaDto, MediaStatus, MediaTarget, UploadedFileDto } from '@app/data/dto';
import { firstValueFrom } from 'rxjs';

export type UploadQueueItemStatus = 'queued' | 'uploading' | 'completed' | 'failed' | 'cancelled';

export interface UploadOptions {
	readonly progressChanged?: (item: UploadQueueItem) => void;
	readonly queueGroupId?: string;
	readonly maxConcurrentUploads?: number;
	readonly showInQueue?: boolean;
	readonly queueDisplayThreshold?: number;
	readonly mediaTarget?: MediaTarget;
}

export class UploadQueueItem {
	progress = 0;
	status: UploadQueueItemStatus = 'queued';
	media: MediaDto | null = null;
	errorMessage: string | null = null;

	constructor(
		readonly uploadId: string,
		readonly file: File,
		readonly queueGroupId: string,
		readonly showInQueue: boolean,
		readonly queueDisplayThreshold: number,
	) {}

	get fileName(): string {
		return this.file.name;
	}

	get isActive(): boolean {
		return this.status === 'uploading';
	}

	get isQueued(): boolean {
		return this.status === 'queued';
	}
}

interface PendingUpload {
	readonly item: UploadQueueItem;
	readonly maxConcurrentUploads: number;
	readonly resolve: (media: MediaDto) => void;
	readonly reject: (error: unknown) => void;
	readonly progressChanged?: (item: UploadQueueItem) => void;
	readonly mediaTarget: MediaTarget;
}

const defaultMaxConcurrentUploads = 3;
const defaultQueueDisplayThreshold = 3;
const uploadChunkSize = 1024 * 1024 * 15;

@Injectable({ providedIn: 'root' })
export class UploadService {
	private readonly mediaApi = inject(MediaApi);
	private readonly items = signal<readonly UploadQueueItem[]>([]);
	private readonly pendingUploads: PendingUpload[] = [];

	readonly uploads = this.items.asReadonly();
	readonly visibleUploads = computed(() => {
		const activeUploads = this.uploads().filter((item) => item.status !== 'completed' && item.status !== 'cancelled');
		return activeUploads.filter((item) => item.showInQueue && this.countVisibleGroupItems(activeUploads, item.queueGroupId) > item.queueDisplayThreshold);
	});
	readonly hasVisibleUploads = computed(() => this.visibleUploads().length > 0);

	upload(file: File, progressChangedOrOptions?: ((item: UploadQueueItem) => void) | UploadOptions): Promise<MediaDto> {
		if (file.size === 0) {
			return Promise.reject(new Error('Empty files cannot be uploaded.'));
		}

		const options = this.normalizeOptions(progressChangedOrOptions);
		const item = new UploadQueueItem(
			crypto.randomUUID(),
			file,
			options.queueGroupId ?? crypto.randomUUID(),
			options.showInQueue ?? true,
			this.normalizePositiveInteger(options.queueDisplayThreshold, defaultQueueDisplayThreshold),
		);
		const maxConcurrentUploads = this.normalizePositiveInteger(options.maxConcurrentUploads, defaultMaxConcurrentUploads);

		const uploadPromise = new Promise<MediaDto>((resolve, reject) => {
			this.pendingUploads.push({ item, maxConcurrentUploads, resolve, reject, progressChanged: options.progressChanged, mediaTarget: options.mediaTarget ?? MediaTarget.Other });
			this.items.update((items) => [...items, item]);
			this.processQueue();
		});

		return uploadPromise;
	}

	cancel(uploadId: string): void {
		const pendingIndex = this.pendingUploads.findIndex((upload) => upload.item.uploadId === uploadId);
		if (pendingIndex >= 0) {
			const [upload] = this.pendingUploads.splice(pendingIndex, 1);
			upload.item.status = 'cancelled';
			upload.reject(new Error('Upload cancelled'));
			this.publish();
			return;
		}

		const item = this.items().find((upload) => upload.uploadId === uploadId);
		if (item?.status === 'uploading') {
			item.status = 'cancelled';
			item.errorMessage = 'Upload cancelled';
			this.publish();
		}
	}

	dismiss(uploadId: string): void {
		this.items.update((items) => items.filter((item) => item.uploadId !== uploadId));
	}

	private processQueue(): void {
		while (this.pendingUploads.length > 0) {
			const uploadIndex = this.pendingUploads.findIndex((upload) => this.countActiveUploads(upload.item.queueGroupId) < upload.maxConcurrentUploads);
			if (uploadIndex === -1) {
				return;
			}

			const [upload] = this.pendingUploads.splice(uploadIndex, 1);
			void this.runUpload(upload);
		}
	}

	private async runUpload(upload: PendingUpload): Promise<void> {
		const { item } = upload;
		item.status = 'uploading';
		item.progress = 0;
		this.notifyProgress(upload);

		try {
			const media = await this.uploadFile(item, upload);
			item.media = media;
			item.progress = 100;
			item.status = 'completed';
			this.notifyProgress(upload);
			upload.resolve(media);
			globalThis.setTimeout(() => this.dismiss(item.uploadId), 2500);
		} catch (error) {
			item.status = this.isCancelled(item) ? 'cancelled' : 'failed';
			item.errorMessage = error instanceof Error && error.message ? error.message : 'Upload failed';
			this.notifyProgress(upload);
			upload.reject(error);
		} finally {
			this.processQueue();
		}
	}

	private async uploadFile(item: UploadQueueItem, upload: PendingUpload): Promise<MediaDto> {
		const totalChunks = Math.max(1, Math.ceil(item.file.size / uploadChunkSize));
		let uploadedFile: UploadedFileDto | null = null;

		for (let chunkNumber = 0; chunkNumber < totalChunks; chunkNumber += 1) {
			if (item.status === 'cancelled') {
				throw new Error('Upload cancelled');
			}

			const start = chunkNumber * uploadChunkSize;
			const end = Math.min(item.file.size, start + uploadChunkSize);
			uploadedFile = await firstValueFrom(this.mediaApi.uploadChunk(item.file.slice(start, end), item.file.name, chunkNumber, totalChunks, false, false, upload.mediaTarget));

			if (uploadedFile.status === MediaStatus.Failed) {
				throw new Error(uploadedFile.message ?? 'Upload failed');
			}

			item.progress = this.clampProgress(uploadedFile.progress ?? Math.round(((chunkNumber + 1) / totalChunks) * 100));
			this.notifyProgress(upload);
		}

		if (!uploadedFile?.mediaId) {
			throw new Error('Upload did not return a media id.');
		}

		return {
			mediaId: uploadedFile.mediaId,
			fileName: uploadedFile.fileName || item.file.name,
		};
	}

	private notifyProgress(upload: PendingUpload): void {
		upload.progressChanged?.(upload.item);
		this.publish();
	}

	private publish(): void {
		this.items.update((items) => [...items]);
	}

	private isCancelled(item: UploadQueueItem): boolean {
		return item.status === 'cancelled';
	}

	private normalizeOptions(progressChangedOrOptions?: ((item: UploadQueueItem) => void) | UploadOptions): UploadOptions {
		return typeof progressChangedOrOptions === 'function'
			? { progressChanged: progressChangedOrOptions }
			: progressChangedOrOptions ?? {};
	}

	private normalizePositiveInteger(value: number | undefined, fallback: number): number {
		if (!Number.isFinite(value) || value == null) {
			return fallback;
		}

		return Math.max(1, Math.round(value));
	}

	private countActiveUploads(queueGroupId: string): number {
		return this.items().filter((item) => item.queueGroupId === queueGroupId && item.status === 'uploading').length;
	}

	private countVisibleGroupItems(items: readonly UploadQueueItem[], queueGroupId: string): number {
		return items.filter((item) => item.showInQueue && item.queueGroupId === queueGroupId).length;
	}

	private clampProgress(progress: number): number {
		if (!Number.isFinite(progress)) {
			return 0;
		}

		return Math.min(100, Math.max(0, Math.round(progress)));
	}
}
